/**
 * Holds the "a new version is available" offer back while a reload would destroy work in progress.
 *
 * A hold is a named claim. While at least one claim is registered the banner stays off screen; the
 * offer is not lost, only deferred, and it appears on its own once the last claim is released
 * (`AppUpdateModule.isUpdateOffered` is derived state, not a timer).
 *
 * Usage from a component:
 * ```ts
 * @Watch("isExporting")
 * onExportingChanged(isExporting: boolean) {
 *   setUpdateHold("hydry-sensor-data-export", isExporting);
 * }
 *
 * beforeDestroy() {
 *   setUpdateHold("hydry-sensor-data-export", false);
 * }
 * ```
 *
 * Always release in `beforeDestroy()`: a component torn down mid-flow (a route change, a logout)
 * would otherwise leave a claim behind and silence the banner for the rest of the session.
 */

import { AppUpdateModule } from "@/store/modules/AppUpdateModule";

/**
 * Registers or releases the hold `id`.
 *
 * Ids must be unique per holder. Use a literal for a screen that exists once at a time, and
 * {@link nextUpdateHoldId} for a component that can be on screen more than once.
 */
export function setUpdateHold(id: string, active: boolean): void {
  if (active) {
    AppUpdateModule.addHold(id);
    return;
  }

  AppUpdateModule.removeHold(id);
}

let holdIdCounter = 0;

/**
 * A unique hold id for a component that can be instantiated several times over (dialogs, list
 * rows). Without this, an inner dialog closing would release the outer dialog's hold.
 */
export function nextUpdateHoldId(prefix: string): string {
  holdIdCounter += 1;

  return `${prefix}#${holdIdCounter}`;
}
